import React, { useEffect, useState } from "react";
import { Icon, Image, Modal, Tabs } from "@nubeteck/components";
import { type TabsProps, Skeleton } from "antd";
import { LargeHeadingStyled, SmallHeadingStyled } from "../../custom/texts/texts.styled";
import ScrollView from "../../custom/scroll-view/scroll-view";
import { useMediaQuery } from "src/hooks";
import { MenuButton } from "src/components/layout/mobile-menu/mobile-menu.style";
import gifMatriculacion from "src/assets/gifs/guide-register.gif";
import gifCalendario from "src/assets/gifs/guide-calendar.gif";
import gifCaja from "src/assets/gifs/guide-cart.gif";
import gifCourses from "src/assets/gifs/guide-courses.gif";
import gifCourseDetail from "src/assets/gifs/guide-course-detail.gif";
import gifActivity from "src/assets/gifs/guide-activity.gif";
import { RootState } from "@/core/store/store";
import { useSelector } from "react-redux";
import { getUserHelpPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../../pages/StrapiError";

const UserHelp = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 1180px)");
  const { isATeacher } = useSelector((state: RootState) => state.user);
  const { locale } = useLocale();
  const [userHelpPage, setUserHelpPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const showModal: () => void = () => {
    setIsModalOpen(true);
  };
  const handleOk: () => void = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setUserHelpPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    getUserHelpPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserHelpPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserHelpPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setUserHelpPage(null);
      }}
    />;
  }

  const items: TabsProps["items"] = isATeacher
    ? [
        {
          key: "1",
          label: (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.introduction_nb || "Introducción"),
          children: (
            <ScrollView height="40vh">
              <>
                <LargeHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.title || "Guía de usuario"}</LargeHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.pagraph1 || 
                    "Esta guía de usuario está diseñada para ayudarle a utilizar el sistema de Cenfotec Autoservicio de manera efectiva. Este apartado proporciona los pasos necesarios para visualizar la información de los cursos que imparte, registrar nuevas actividades, y reportar calificaciones y asistencia."
                  )}
                </p>
              </>
            </ScrollView>
          ),
        },
        {
          key: "2",
          label: (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.view_courses_sb || "Visualización de cursos"),
          children: (
            <ScrollView height="40vh">
              <>
                <LargeHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_courses_heading || "Cursos"}</LargeHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_courses_p1 || 
                    "El apartado de cursos muestra los cursos impartidos en los periodos actuales, con la posibilidad de filtrar otros cursos dependiendo el período seleccionado en el campo ubicado en la parte superior izquierda de la vista."
                  )}
                </p>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_courses_p2 || 
                    "Haciendo clic en las opciones de la parte superior derecha es posible visualizar los cursos en forma de lista, o en mosaicos; también cuenta con la opción de exportar a Excel la lista de cursos filtrados."
                  )}
                </p>
                <Image src={gifCourses} alt="Pasos lista de cursos" preview={false} />
                <SmallHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_courses_options_title || "¿Qué opciones tiene disponible cada curso?"}</SmallHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_courses_options_p1 || 
                    "Si se encuentra en la vista de mosaicos, puede acceder al detalle de un curso haciendo clic en la imagen del mismo. Debajo se muestran las opciones de ver el curso en Moodle, ir a las calificaciones, o ir al reporte de asistencia del curso."
                  )}
                </p>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_courses_options_p2 || 
                    "Si se encuentra en la vista tipo lista, las opciones se muestran al dar clic a los tres puntos ubicados al final del registro."
                  )}
                </p>
                <Image src={gifCourseDetail} alt="Pasos lista de cursos" preview={false} />
                <SmallHeadingStyled>
                  {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_course_detail_title || "¿Qué información contiene el detalle del curso?"}
                </SmallHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_course_detail_p1 || 
                    "En el detalle del curso será posible visualizar la información general del curso, la lista de estudiantes, el horario del curso y las actividades registradas."
                  )}
                </p>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    <>
                      {userHelpPage?.user_help_course_detail_p4 ||
                        "Puede navegar hacia las calificaciones de un estudiante haciendo clic en el ícono "}
                      <Icon name="" outlined color="#A7A7A7" size={13} />
                      {userHelpPage?.user_help_course_detail_p6 ||
                        " ubicado al final del registro. También puede acceder a las calificaciones de una actividad en concreto haciendo clic en el mismo ícono en la lista de actividades."}
                    </>
                  )}
                </p>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_course_detail_p5 || 
                    "En la lista de actividades ubicada al final del detalle, es posible la creación, edición e inhabilitación de actividades. También se dispone de una opción de editar los puntos posibles de cada actividad en la lista de forma rápida."
                  )}
                </p>
              </>
            </ScrollView>
          ),
        },
        {
          key: "3",
          label: (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.activity_register_sb || "Registro de actividades"),
          children: (
            <ScrollView height="40vh">
              <>
                <LargeHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_activity_heading || "Registro de actividades"}</LargeHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_activity_p1 || 
                    "Al hacer clic en 'Nueva actividad', se desplegará un formulario a la derecha con los campos necesarios para crear una actividad. Debe tomar en cuenta que al intentar registrar una actividad, si la sumatoria de puntos de las actividades supera los 100 puntos, no será posible guardar la nueva actividad."
                  )}
                </p>
                <Image src={gifActivity} alt="Registro de actividad" preview={false} />
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_activity_p2 || 
                    "El formulario se divide en dos secciones: la información general de la actividad, y los datos asociados a la actividad en Moodle. En caso de que la actividad aún no se encuentre registrada en Moodle, puede dirigirse a la creación de la misma, para el tipo de recurso y semana indicados, haciendo clic en 'Crear actividad'."
                  )}
                </p>
              </>
            </ScrollView>
          ),
        },
        {
          key: "4",
          label: (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.asistence_repot_sb || "Reporte de asistencia"),
          children: (
            <ScrollView height="40vh">
              <>
                <LargeHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_attendance_heading || "Reporte de asistencia"}</LargeHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_attendance_p1 || 
                    "Para ver el listado de asistencia es necesario seleccionar una fecha. Una vez mostrada la lista de clase, tiene la opción de realizar una búsqueda por el nombre del estudiante, o modificar los estatus de asistencia de cada estudiante."
                  )}
                </p>
                <Image src={gifActivity} alt="Reporte de asistencia" preview={false} />
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_attendance_p2 || 
                    "Puede seleccionar varios estudiantes marcando las casillas al inicio de cada registro. Una vez hecho esto, se habilitará un campo encima de la lista, en el que puede seleccionar cuál será el estatus de asistencia para los estudiantes seleccionados. Guarde los cambios haciendo clic en el botón azul en la parte superior derecha de la vista."
                  )}
                </p>
              </>
            </ScrollView>
          ),
        },
        {
          key: "5",
          label: (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.calification_report_sb || "Reporte de calificaciones"),
          children: (
            <ScrollView height="40vh">
              <>
                <LargeHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_grades_heading || "Reporte de calificaciones"}</LargeHeadingStyled>
                <Image src={gifCourseDetail} alt="Reporte de calificaciones" preview={false} />
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_grades_p1 || 
                    "En cada vista de calificaciones de las que se dispone, puede seleccionar el método de puntuación: 'Escala 100' para una calificación porcentual, o 'Puntaje' para calificar según el límite de puntos establecidos."
                  )}
                </p>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_grades_p2 || 
                    "Una vez reportada la calificación de todos los estudiantes, para cada actividad registrada, tendrá la opción de cerrar el acta de calificaciones."
                  )}
                </p>
              </>
            </ScrollView>
          ),
        },
      ]
    : [
        {
          key: "1",
          label: (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.introduction_nb || "Introducción"),
          children: (
            <ScrollView height="40vh">
              <>
                <LargeHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.title || "Guía de usuario"}</LargeHeadingStyled>
                <p>
                {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.pagraph1 || 
                    "Esta guía de usuario está diseñada para ayudarle a utilizar el sistema de Cenfotec Autoservicio de manera efectiva. Este apartado proporciona los pasos necesarios para visualizar la información de los cursos que imparte, registrar nuevas actividades, y reportar calificaciones y asistencia."
                  )}
                </p>
              </>
            </ScrollView>
          ),
        },
        {
          key: "2",
          label: (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.enrrollment_nb || "Proceso de matrícula"),
          children: (
            <ScrollView height="40vh">
              <>
                <LargeHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_enrollment_heading || "Proceso de matrícula"}</LargeHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_enrollment_p1 || 
                    "La sección para que un estudiante pueda matricularse estará disponible siempre que el período de selección esté abierto. Para comenzar, debe ir al enlace 'Matrícula' ubicado en el menú superior."
                  )}
                </p>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_enrollment_p2 || 
                    "El primer paso es seleccionar el programa para el cual se realizará la selección de materias. En caso de que no se escoja un programa, se mostrará una advertencia."
                  )}
                </p>
                <Image src={gifMatriculacion} alt="Pasos matrícula" preview={false} />
                <SmallHeadingStyled>
                  {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_enrollment_how_select_title || "¿De qué forma puedo escoger las materias que me corresponden?"}
                </SmallHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_enrollment_p3 || 
                    "Las materias que se mostrarán serán solo las que pertenezcan al periodo en el que se encuentra, o que por otros motivos le corresponde cursarla."
                  )}
                </p>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_enrollment_p4 || 
                    "Puede elegir desde el listado de materias que se encuentra a la izquierda, para ver la descripción de la materia, y las secciones disponibles."
                  )}
                </p>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_enrollment_p5 || 
                    "Para escoger una sección, puede optar por la vista llamada 'Oferta', o puede usar 'Oferta en vista de calendario'. Utilizar una vista u otra no afectará el resultado de su selección."
                  )}
                </p>
                <SmallHeadingStyled>
                  {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_enrollment_red_title || "¿Qué quiere decir una sección que esté subrayada en rojo?"}
                </SmallHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_enrollment_p6 || 
                    "Esto quiere decir que la sección está disponible, pero su horario interfiere con el de otra materia ya seleccionada."
                  )}
                </p>
              </>
            </ScrollView>
          ),
        },
        {
          key: "3",
          label: (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.facturation_nb || "Facturación"),
          children: (
            <ScrollView height="40vh">
              <>
                <LargeHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_billing_heading || "Caja y facturación"}</LargeHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_billing_p1 || 
                    "En el apartado de caja se facturan las materias seleccionadas en el proceso de matrícula, los cargos o deudas pendientes con la universidad, y/o los servicios solicitados por usted mismo(a)."
                  )}
                </p>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_billing_p2 || 
                    "En el lado izquierdo podrá observar el listado de asignaturas que se deben facturar, seccionadas por programa, y los servicios obligatorios o solicitados por usted; al lado derecho podrá ubicar el resumen de todos los cargos, descuentos y pagos relacionados a la factura."
                  )}
                </p>
                <Image src={gifCaja} alt="Pasos matrícula" preview={false} />
                <SmallHeadingStyled>
                  {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_billing_partial_title || "¿Qué pasa si quiero pagar una cantidad distinta al total de la factura?"}
                </SmallHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_billing_p3 || 
                    "Tiene la opción de pagar en cuotas una o varias asignaturas."
                  )}
                </p>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_billing_p4 || 
                    "Si tiene una o más materias para pagar en cuotas, se habilitará un espacio donde puede escribir el monto que desea pagar inicialmente. El monto digitado no puede ser menor al total que debe pagar por los cargos no asignados para cuotas. Si el monto que digita sobrepasa el total pendiente, el exceso será abonado a su primera cuota."
                  )}
                </p>
                <SmallHeadingStyled>
                  {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_billing_transfer_title || "He cubierto una parte del pago por medio de una transferencia bancaria, ¿Qué puedo hacer?"}
                </SmallHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_billing_p5 || 
                    `En el resumen de su factura encontrará un apartado en el que se muestran la beca o patrocinio que originan un descuento. Si ha realizado una transferencia bancaria para el pago de la factura correspondiente, puede registrarlo haciendo clic en ${userHelpPage?.user_help_billing_transfer_button || "PAGO POR TRANSFERENCIA"}."`
                  )}
                </p>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_billing_p6 || 
                    "Una vez registrada la transferencia no será posible modificar los datos. El aval de la transferencia será revisado, y sus asignaturas seleccionadas quedarán en un estado de espera hasta que se confirmen los datos ingresados."
                  )}
                </p>
              </>
            </ScrollView>
          ),
        },
        {
          key: "5",
          label: (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.canlendar_nb || "Calendario"),
          children: (
            <ScrollView height="40vh">
              <>
                <LargeHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_calendar_heading || "Calendario"}</LargeHeadingStyled>
                <p>
                  {isLoadingContent ? (
                    <Skeleton.Input active size="default" style={{ width: 100 }} />
                  ) : (
                    userHelpPage?.user_help_calendar_p1 || 
                    "En este apartado tendrá acceso al horario de las materias seleccionadas oficialmente."
                  )}
                </p>
                <Image src={gifCalendario} alt="Funcionamiento calendario" preview={false} />
              </>
            </ScrollView>
          ),
        },
      ];

  return (
    <>
      {isMobile ? (
        <MenuButton type="text" withIcon icon={<Icon name="help" />} onClick={showModal}>
          {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.user_help_button_label || "Ayuda"}
        </MenuButton>
      ) : (
        <Icon isTouchable={true} onClick={showModal} name="help" color="white" />
      )}

      <Modal
        title={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.title || "Guía de usuario"}
        open={isModalOpen}
        onOk={handleOk}
        okText={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : userHelpPage?.close_button || "Cerrar"}
        cancelButtonProps={{ style: { display: "none" } }}
        width={800}
        onCancel={handleOk}
      >
        <br />
        <Tabs
          defaultActiveKey="1"
          tabPosition={isMobile ? "top" : "left"}
          items={items}
          style={{ height: "45vh" }}
        />
      </Modal>
    </>
  );
};

export default UserHelp;
